<template>
  <v-card class="pa-2 mb-5">
    <v-toolbar class="mb-2">
      <v-layout align-center justify-space-between>
        <span class="text-h5">Promise-to-Pay</span>
        <v-spacer />
        <v-btn
          data-testid="ptp-stepper-btn"
          class="success"
          outlined
          v-if="ledgers && ledgers.length"
          @click.native="sendToPromiseStepper"
        >
          <v-icon class="mr-3">payment</v-icon>
          Schedule Promise-to-Pay
        </v-btn>
      </v-layout>
    </v-toolbar>
    <v-flex
      v-if="!promiseToPays || !promiseToPays.length"
      class="subheading mx-3 mt-4 mb-2"
      data-testid="no-promise-to-pays-message"
    >
      There are no active promise-to-pays for {{ tenant.first_name }} {{ tenant.last_name }}.
    </v-flex>
    <v-layout v-else column>
      <v-expansion-panels
          data-testid="tenant-promises-expansion-panel"
          class="tenant-promises-expansion-panel my-2 secondary"
          v-model="selectedPromise"
          focusable
      >
        <v-expansion-panel
          :data-testid="`tenant-rentals-unit-${promise.unit.name}-expansion-panel`"
          :class="`tenant-rentals-unit-${promise.unit.name}-expansion-panel`"
          v-for="(promise, i) in promiseToPays"
          :key="i"
        >
        <v-expansion-panel-header>
          <h3
            slot="header"
            title
            @click="resetData"
            data-testid="panel-header"
          >
            Unit {{ promise.unit.name }}
          </h3>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>
            <v-card-text>
              <v-layout
                column
                class="subheading my-1"
                justify-space-between
              >
                <tenant-rental-details
                  :ledger="activeLedger"
                  :loading="unitDetailsLoading"
                  :unitFeatures="unitFeatures"
                  :promiseToPay="activePromiseToPay"
                  isPromise
                />
                <v-layout column>
                  <payment-history
                    v-if="paymentHistory && paymentHistory.length"
                    :paymentHistory="paymentHistory"
                    @hideHistory="paymentHistory = []"
                  />
                  <ledger-notes
                    v-if="ledgerNotes && ledgerNotes.length"
                    :ledgerNotes="ledgerNotes"
                    @hideNotes="ledgerNotes = []"
                  />
                  <ledger-statement
                    v-if="ledgerStatement && ledgerStatement.length"
                    :ledgerStatement="ledgerStatement"
                    @hideStatement="ledgerStatement = []"
                  />
                </v-layout>
              </v-layout>
              <div v-if="promise.status !== 'completed'">
                <v-divider class="my-3" />
                <v-layout justify-space-between>
                  <v-flex xs2 class="px-3">
                    <update-billing :ledger="activeLedger" @success="billingUpdate" />
                  </v-flex>
                  <v-flex xs2 class="px-3">
                    <v-btn
                      data-testid="promise-payment-history-btn"
                      class="promise-payment-history-btn teal large white--text pl-2 pr-4"
                      block
                      :loading="historyLoading"
                      :disabled="!!ledgerNotes.length || !!ledgerStatement.length"
                      @click.native="toggleComponent(activeLedger.ledger_id, 'paymentHistory')"
                    >
                      <v-icon class="mr-2">attach_money</v-icon>
                      <span v-if="!paymentHistory || !paymentHistory.length">Get Payment History</span>
                      <span v-else>Hide Payment History</span>
                    </v-btn>
                  </v-flex>
                  <v-flex xs2 class="px-3">
                    <v-btn
                      data-testid="promise-ledger-statement-btn"
                      class="promise-ledger-statement-btn deep-orange large white--text pl-3 pr-4"
                      block
                      :loading="statementLoading"
                      :disabled="!!ledgerNotes.length || !!paymentHistory.length"
                      @click.native="toggleComponent(activeLedger.ledger_id, 'ledgerStatement')"
                    >
                      <v-icon class="mr-2">receipt</v-icon>
                      <span v-if="!ledgerStatement || !ledgerStatement.length">Get Statement</span>
                      <span v-else>Hide Statement</span>
                    </v-btn>
                  </v-flex>
                  <v-flex xs2 class="px-3">
                    <v-btn
                      data-testid="promise-ledger-notes-btn"
                      class="promise-ledger-notes-btn primary large white--text pl-3 pr-4"
                      block
                      :loading="notesLoading"
                      :disabled="!!ledgerStatement.length || !!paymentHistory.length"
                      @click.native="toggleComponent(activeLedger.ledger_id, 'ledgerNotes')"
                    >
                      <v-icon class="mr-2">notes</v-icon>
                      <span v-if="!ledgerNotes || !ledgerNotes.length">Get Notes</span>
                      <span v-else>Hide Notes</span>
                    </v-btn>
                  </v-flex>
                  <v-flex xs2 class="px-3">
                    <create-note :ledgerId="activeLedger.ledger_id" :ledgers="ledgers" />
                  </v-flex>
                  <v-flex xs2 class="px-3">
                    <cancel-payment-promise :planId="activePlanId" />
                  </v-flex>
                </v-layout>
              </div>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-layout>
  </v-card>
</template>

<script>
import CancelPaymentPromise from "@/modules/accounts-receivable/components/CancelPaymentPromise";
import CreateNote from "@/components/tenant-rentals/actions/CreateNote";
import LedgerNotes from "@/components/tenant-rentals/actions/LedgerNotes";
import LedgerStatement from "@/components/tenant-rentals/actions/LedgerStatement";
import PaymentHistory from "@/components/tenant-rentals/actions/PaymentHistory";
import TenantRentalDetails from "@/components/tenant-rentals/partials/TenantRentalDetails";
import UpdateBilling from "@/components/tenant-rentals/actions/UpdateBilling";
import { mapState } from "vuex";
import router from "@/router";

export default {
  name: "TenantPromises",
  props: {
    convenienceFeeStatus: {
      type: [String, null]
    },
    activeTab: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      notesLoading: false,
      statementLoading: false,
      unitDetailsLoading: false,
      historyLoading: false,

      ledgerNotes: [],
      ledgerStatement: [],
      paymentHistory: [],

      selectedPromise: -1
    };
  },

  methods: {
    billingUpdate() {
      this.$emit("billingUpdate");
    },

    sendToPromiseStepper() {
      router.push({
        name: "promiseToPay",
        params: {
          facilityId: this.$route.params.facilityId,
          tenantId: this.$route.params.tenantId
        }
      }).catch(() => {});
    },

    getUnitDetails(unitId) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: unitId
      };
      this.unitDetailsLoading = true;
      this.$store.dispatch("getUnitFeatures", payload)
        .then(() => {
          this.unitDetailsLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.unitDetailsLoading = false;
        });
    },

    toggleComponent(ledgerId, type) {
      if (this[type] && this[type].length) {
        this[type] = [];
      } else if (type === "paymentHistory") {
        this.getPaymentHistory(ledgerId);
      } else if (type === "ledgerNotes") {
        this.getNotes(ledgerId);
      } else if (type === "ledgerStatement") {
        this.getStatement(ledgerId);
      }
    },

    getPaymentHistory(ledgerId) {
      this.historyLoading = true;
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };
      this.$store.dispatch("getPaymentHistory", payload)
        .then(resp => {
          this.paymentHistory = resp.data;
          this.historyLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.historyLoading = false;
        });
    },

    getNotes(ledgerId) {
      this.notesLoading = true;
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };
      this.$store.dispatch("getLedgerNotes", payload)
        .then(resp => {
          this.notesLoading = false;
          resp.data ? this.ledgerNotes = resp.data : this.ledgerNotes = [];
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.notesLoading = false;
        });
    },

    getStatement(ledgerId) {
      this.statementLoading = true;
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };
      this.$store.dispatch("getLedgerStatement", payload)
        .then(resp => {
          this.statementLoading = false;
          this.ledgerStatement = resp.data;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.statementLoading = false;
        });
    },

    resetData() {
      this.ledgerNotes = [];
      this.ledgerStatement = [];
    }
  },

  computed: {
    ...mapState({
      ledgers: state => state.tenant.ledgers,
      promiseToPays: state => state.accountsReceivable.promiseToPays,
      tenant: state => state.tenant.info,
      unitFeatures: state => state.facilities.unitFeatures
    }),

    activeLedger() {
      return this.activePromise ? this.ledgers.find(ledger => ledger.unit_id === this.activePromise.unit_id) : "";
    },

    activePromiseToPay() {
      return this.activePromise ? this.promiseToPays.find(unit => unit.unit_id === this.activePromise.unit_id) : "";
    },

    activePromise() {
      if (this.selectedPromise > -1) {
        return this.promiseToPays[this.selectedPromise];
      } else {
        return "";
      }
    },

    activePlanId() {
      return this.activePromise ? this.activePromise.id : -1;
    }
  },

  watch: {
    selectedPromise() {
      if (this.activePromise) {
        this.getUnitDetails(this.activePromise.unit_id);
      }
    },
    activeTab() {
      if (this.activeTab !== 2) {
        this.selectedPromise = -1;
      }
    }
  },

  components: {
    CancelPaymentPromise,
    CreateNote,
    LedgerNotes,
    LedgerStatement,
    PaymentHistory,
    UpdateBilling,
    TenantRentalDetails
  }
};
</script>
